import { migrateLegacyPlayground } from '@/lib/playground-storage';
import { isValidHex, normalizeHex } from '@/lib/studio-color';
import { OFFICIAL_PRESETS } from '@/data/official-presets';
import { PRESETS } from '@/data/presets';
import { parseCustomPresetData } from '@/lib/custom-presets';
import { isRecord } from '@/lib/persist';
import { ensureAvailablePreset, parseSettings } from '@/lib/storage';
import { parseStudioState } from '@/lib/studio-storage';
import type { GeneratorSettings, PresetRegistry } from '@/types/palette';
import type { StudioState } from '@/types/studio';

export interface StudioDocument {
  studio: StudioState;
  settings: GeneratorSettings;
  customPresets: PresetRegistry;
}

export const MAX_PROJECT_BYTES = 2_000_000;
const normalize = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(normalize)
    : isRecord(value)
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, normalize(value[key])]),
        )
      : value;
const sameData = (a: unknown, b: unknown) =>
  JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));

export function serializeProject(document: StudioDocument): string {
  return JSON.stringify(
    {
      kind: 'color-studio-workspace',
      version: 2,
      document: { ...document, studio: parseStudioState(document.studio).value },
    },
    null,
    2,
  );
}

export function parseProject(text: string): StudioDocument {
  if (text.length > MAX_PROJECT_BYTES)
    throw new Error('Choose a workspace backup smaller than 2 MB.');
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('This backup is not valid JSON.');
  }
  if (
    !isRecord(data) ||
    data.kind !== 'color-studio-workspace' ||
    (data.version !== 1 && data.version !== 2) ||
    !isRecord(data.document)
  )
    throw new Error('Choose a Color Studio workspace backup (version 1 or 2).');
  let candidate = data.document;
  if (data.version === 1) {
    const oldStudio = isRecord(candidate.studio) ? candidate.studio : {};
    const old = isRecord(oldStudio.playground) ? oldStudio.playground : {};
    const oldRoles = isRecord(old.roles) ? old.roles : {};
    const roleIds = ['background', 'surface', 'text', 'primary', 'secondary', 'accent'];
    if (
      Object.keys(old).length !== 4 ||
      !['roles', 'template', 'radius', 'vision'].every((key) => key in old) ||
      Object.keys(oldRoles).length !== roleIds.length ||
      !roleIds.every(
        (key) =>
          typeof oldRoles[key] === 'string' &&
          isValidHex(oldRoles[key]) &&
          normalizeHex(oldRoles[key]) === oldRoles[key],
      ) ||
      !['interface', 'editorial', 'poster'].includes(String(old.template))
    )
      throw new Error(
        'This backup has missing or invalid values. Your current workspace has not changed.',
      );
    candidate = {
      ...candidate,
      studio: { ...oldStudio, playground: migrateLegacyPlayground(old) },
    };
  }
  const studio = parseStudioState(candidate.studio);
  const settings = parseSettings(candidate.settings);
  if (
    studio.discarded ||
    settings.discarded ||
    !sameData(studio.value, candidate.studio) ||
    !sameData(settings.value, candidate.settings) ||
    !isRecord(candidate.customPresets)
  )
    throw new Error(
      'This backup has missing or invalid values. Your current workspace has not changed.',
    );
  const customPresets = Object.keys(candidate.customPresets).length
    ? parseCustomPresetData(candidate.customPresets, [
        ...Object.keys(PRESETS),
        ...Object.keys(OFFICIAL_PRESETS),
      ])
    : {};
  const available = ensureAvailablePreset(settings.value, {
    ...PRESETS,
    ...OFFICIAL_PRESETS,
    ...customPresets,
  });
  if (!sameData(available, settings.value) || !sameData(customPresets, candidate.customPresets))
    throw new Error(
      'This backup has an invalid scale preset. Your current workspace has not changed.',
    );
  return { studio: studio.value, settings: settings.value, customPresets };
}
